// =============================================================================
// hooks/kanban/useKanbanCards.ts — Board paginado por estágio (F55-S08).
//
// Antes: um GET global cortado em 50 cards por prioridade, e o contador de cada
// coluna era `cards.length` — colunas inteiras apareciam com 0 (CRM-01).
//
// Agora:
//   - cada coluna é uma query própria (`stage_id` + cursor), páginas de 50;
//   - o contador vem de GET /api/kanban/cards/counts (mesmo WHERE da lista);
//   - "Carregar mais" acrescenta a próxima página ao acumulado da coluna;
//   - refetch (foco, invalidação) recarrega TANTAS páginas quanto já estavam
//     visíveis — a coluna não encolhe de 300 para 50 ao voltar à aba.
//
// Chaves sob o prefixo ['kanban','cards'] — quem invalida esse prefixo
// (LeadActionButtons, useMoveCard) atinge colunas E contagem.
// =============================================================================

import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import * as React from 'react';

import { api } from '../../lib/api';

import {
  KANBAN_PAGE_SIZE,
  appendPage,
  countsToRecord,
  filtersToParams,
  isEmptyLeadFilter,
  normalizeFilters,
  stagePageParams,
} from './board';
import type { KanbanCardsPage, KanbanCountsResponse, StageCards, StageCounts } from './board';
import type { KanbanCard, KanbanFilters } from './types';

/** Prefixo de tudo que é card do board (colunas + contagem), por filtros. */
export const KANBAN_CARDS_KEY = (filters: KanbanFilters) => ['kanban', 'cards', filters] as const;

export const KANBAN_STAGE_CARDS_KEY = (filters: KanbanFilters, stageId: string) =>
  ['kanban', 'cards', filters, { stageId }] as const;

export const KANBAN_COUNTS_KEY = (filters: KanbanFilters) =>
  ['kanban', 'cards', filters, 'counts'] as const;

/** Teto de página da API — usado só para recarregar o que já estava visível. */
const MAX_PAGE_LIMIT = 100;

async function fetchStagePage(
  filters: KanbanFilters,
  stageId: string,
  cursor: string | null,
  limit: number,
): Promise<KanbanCardsPage> {
  const qs = stagePageParams(filters, stageId, cursor, limit).toString();
  return api.get<KanbanCardsPage>(`/api/kanban/cards?${qs}`);
}

/**
 * Busca a coluna inteira até cobrir o que já estava carregado (mínimo uma
 * página). Mesmo comportamento de refetch de uma infinite query.
 */
async function fetchStage(
  queryClient: QueryClient,
  filters: KanbanFilters,
  stageId: string,
): Promise<StageCards> {
  const prev = queryClient.getQueryData<StageCards>(KANBAN_STAGE_CARDS_KEY(filters, stageId));
  const want = Math.max(prev?.cards.length ?? 0, KANBAN_PAGE_SIZE);

  let acc: StageCards = { cards: [], nextCursor: null };
  let cursor: string | null = null;
  do {
    const limit = Math.min(MAX_PAGE_LIMIT, Math.max(1, want - acc.cards.length));
    const page = await fetchStagePage(filters, stageId, cursor, limit);
    acc = appendPage(acc, page);
    cursor = page.nextCursor;
  } while (cursor !== null && acc.cards.length < want);
  return acc;
}

async function fetchCounts(filters: KanbanFilters): Promise<StageCounts> {
  const qs = filtersToParams(filters).toString();
  const res = await api.get<KanbanCountsResponse>(`/api/kanban/cards/counts${qs ? `?${qs}` : ''}`);
  return countsToRecord(res);
}

export interface KanbanBoard {
  /** Filtros normalizados — a MESMA chave que useMoveCard precisa usar. */
  filters: KanbanFilters;
  cardsByStage: Record<string, KanbanCard[]>;
  hasMoreByStage: Record<string, boolean>;
  /** Total por coluna (servidor). `undefined` enquanto carrega. */
  counts: StageCounts | undefined;
  isLoading: boolean;
  isError: boolean;
  /** Carrega a próxima página da coluna. Rejeita em erro de rede. */
  loadMore: (stageId: string) => Promise<void>;
}

/**
 * Board do Kanban: uma query por coluna + uma de contagem.
 *
 * `filters.leadIds = []` (filtro rápido sem nenhum lead) não consulta o
 * servidor: colunas vazias e contadores zerados.
 */
export function useKanbanBoard(stageIds: string[], rawFilters: KanbanFilters = {}): KanbanBoard {
  const queryClient = useQueryClient();
  // Chave estável: o objeto de filtros muda de identidade a cada render do pai.
  const filtersJson = JSON.stringify(normalizeFilters(rawFilters));
  const filters = React.useMemo<KanbanFilters>(
    // `as`: o JSON veio de um KanbanFilters normalizado logo acima.
    () => JSON.parse(filtersJson) as KanbanFilters,
    [filtersJson],
  );
  const empty = isEmptyLeadFilter(filters);

  const stages = useQueries({
    queries: stageIds.map((stageId) => ({
      queryKey: KANBAN_STAGE_CARDS_KEY(filters, stageId),
      queryFn: () => fetchStage(queryClient, filters, stageId),
      staleTime: 15_000,
      enabled: !empty,
    })),
  });

  const countsQuery = useQuery({
    queryKey: KANBAN_COUNTS_KEY(filters),
    queryFn: () => fetchCounts(filters),
    staleTime: 15_000,
    enabled: !empty,
  });

  const cardsByStage: Record<string, KanbanCard[]> = {};
  const hasMoreByStage: Record<string, boolean> = {};
  stageIds.forEach((stageId, i) => {
    const data = stages[i]?.data;
    cardsByStage[stageId] = data?.cards ?? [];
    hasMoreByStage[stageId] = data !== undefined && data.nextCursor !== null;
  });

  const loadMore = React.useCallback(
    async (stageId: string): Promise<void> => {
      const key = KANBAN_STAGE_CARDS_KEY(filters, stageId);
      const prev = queryClient.getQueryData<StageCards>(key);
      if (prev === undefined || prev.nextCursor === null) return;
      const cursor = prev.nextCursor;
      const page = await fetchStagePage(filters, stageId, cursor, KANBAN_PAGE_SIZE);
      // Só acrescenta se a coluna não foi recarregada no meio do caminho
      // (o cursor antigo não descreve mais o fim da lista).
      queryClient.setQueryData<StageCards>(key, (cur) =>
        cur !== undefined && cur.nextCursor === cursor ? appendPage(cur, page) : cur,
      );
    },
    [filters, queryClient],
  );

  return {
    filters,
    cardsByStage,
    hasMoreByStage,
    counts: empty ? {} : countsQuery.data,
    isLoading: !empty && (countsQuery.isLoading || stages.some((q) => q.isLoading)),
    isError: countsQuery.isError || stages.some((q) => q.isError),
    loadMore,
  };
}
